import { useEffect } from 'react';
import { useNavigateBack } from 'hooks';
import { useMutation } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button, FormControl, FormCurrencyField, InputAdornment } from 'components/common';
import { IBenefitsAndCreditsForm } from './types';
import { ApplicationFormPageEnum } from '../types';
import { benefitsAndCreditsQuestions } from './questions';
import { parseApplicationInProgress } from '../helpers';
import { getCurrentRoute } from 'utils/location';
import { applicationFormApi } from 'api/applicationForm';
import { useApplicationInProgressById } from 'queries/applications/useApplicationInProgressById';

export const BenefitsAndCredits = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigateBack = useNavigateBack();
  const formApplicantNames = searchParams.get('applicantNames')?.split(',');
  const activeApplicant = parseInt(searchParams.get('activeApplicant') || '');
  const { id = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(id);
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  const form = useForm<IBenefitsAndCreditsForm>({
    defaultValues: {
      childBenefits: '',
      hasChildBenefits: null,
      disabilityAllowance: '',
      hasDisabilityAllowance: null,
      taxCredits: '',
      hasTaxCredits: null,
    },
  });
  const formState = form.watch();

  useEffect(() => {
    if (applicationInProgress?.[currentRoute]) {
      const { childBenefits, disabilityAllowance, taxCredits } = parseApplicationInProgress(
        applicationInProgress[currentRoute],
      );

      form.reset({
        childBenefits,
        hasChildBenefits: childBenefits ? 'true' : 'false',
        disabilityAllowance,
        hasDisabilityAllowance: disabilityAllowance ? 'true' : 'false',
        taxCredits,
        hasTaxCredits: taxCredits ? 'true' : 'false',
      });
    }
  }, [applicationInProgress]);

  useEffect(() => {
    if (!formState.hasChildBenefits && formState.childBenefits) return form.setValue('childBenefits', '');
    if (!formState.hasDisabilityAllowance && formState.disabilityAllowance)
      return form.setValue('disabilityAllowance', '');
    if (!formState.hasTaxCredits && formState.taxCredits) return form.setValue('taxCredits', '');
  }, [form, formState]);

  const submitFormHandler = async (formData: IBenefitsAndCreditsForm) => {
    const isLastApplicant = formApplicantNames?.length && activeApplicant === formApplicantNames?.length;
    const newActiveAppicant = isLastApplicant ? 1 : activeApplicant + 1;
    const nextPage = isLastApplicant ? ApplicationFormPageEnum.DEPENDANTS : ApplicationFormPageEnum.EMPLOYMENT;

    const { childBenefits, disabilityAllowance, taxCredits } = formData;

    const questions = Object.entries({ childBenefits, disabilityAllowance, taxCredits }).map(([key, answer]) => ({
      question: intl.formatMessage({ id: benefitsAndCreditsQuestions[key].labelKey }),
      key,
      answer: answer || 0,
    }));

    await patchApplicationInProgress({ questions, currentRoute, applicationId: id });

    if (!isLastApplicant) searchParams.delete('hasSecondJob');
    searchParams.set('activeApplicant', `${newActiveAppicant}`);
    searchParams.set('section', nextPage);

    setSearchParams(searchParams);
  };

  const isNextButtonDisabled = [
    formState.hasChildBenefits,
    formState.hasDisabilityAllowance,
    formState.hasTaxCredits,
  ].some(value => value === '');

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <FormattedMessage id={'applicationForm.BENEFITS_AND_CREDITS.subHeader'} tagName={'h4'} />
        {Object.entries(benefitsAndCreditsQuestions).map(([key, question]) => (
          <section key={key} className={key}>
            <FormattedMessage id={question.labelKey} tagName={'h4'} />
            {question.explanationKey && <FormattedMessage id={question.explanationKey} tagName={'p'} />}

            {!formState[question.booleanQuestionFormKey as keyof IBenefitsAndCreditsForm] ||
            formState[question.booleanQuestionFormKey as keyof IBenefitsAndCreditsForm] === 'false' ? (
              <FormControl
                rules={{ required: requiredFieldErrorMessage }}
                name={question.booleanQuestionFormKey}
                radioOptions={question.options}
                radioGroupProps={{ className: 'mt-2' }}
                onChangeEffect={(event: React.ChangeEvent<HTMLInputElement>) => {
                  form.setValue(question.booleanQuestionFormKey, event.target.value);
                }}
              />
            ) : (
              <FormCurrencyField
                name={key}
                rules={{
                  required: requiredFieldErrorMessage,
                  validate: value => Number(value) >= 0,
                }}
                textFieldProps={{
                  InputProps: {
                    endAdornment: (
                      <InputAdornment position='end'>
                        <FormattedMessage id={question.adornmentKey} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          </section>
        ))}

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'} disabled={isNextButtonDisabled}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
